import { useEffect, useState, type FunctionComponent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { initialize as initializeAnalytics } from '@fff-web/fff-analytics';
import { LoadingSpinner } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import { cancelFlowSurveyQuery } from '../queries/cancelFlowSurveyQuery';
import { dynamicTextQuery } from '../queries/dynamicTextQuery';
import reactQueryConfig from '../queries/reactQueryConfig';
import { swapCreditModalContentQuery } from '../queries/sanityContentQuery';
import { pages } from '../utils/pages';
import { paginate } from '../utils/paginator';
import { redirectToTrialCancel } from '../utils/redirectMethods';
import AnalyticsWrapper from './AnalyticsWrapper';
import CancelSubscriptionFinal from './cancel-subscription-final';
import Container from './container/Container';
import { DynamicTextContext } from './context/DynamicTextContext';
import { KeepSubscriptionsContextProvider } from './context/KeepSubscriptionsContext';
import { MappedAnswer, SurveyPageContext } from './context/SurveyPageContext';
import Layout from './layout';
import PrivateRoute from './private-route';
import SwapMyBox from './swap-my-box';

initializeAnalytics();

const App: FunctionComponent = () => {
  const { userInfo } = useUserContext();
  const subscriptionType = userInfo.user?.subscriptionType;
  const subscriptionState = userInfo.user?.subscriptionState;

  useEffect(() => {
    if (subscriptionType === 'TRIAL') {
      redirectToTrialCancel();
    }
  }, [subscriptionType]);

  const { data: survey, isInitialLoading: isCancelFlowSurveyLoading } = useQuery({
    queryKey: [cancelFlowSurveyQuery.key],
    queryFn: cancelFlowSurveyQuery.query,
    ...reactQueryConfig,
  });
  const [surveyPage, setSurveyPage] = useState(0);
  const [mappedAnswers, setMappedAnswers] = useState<Map<number, MappedAnswer>>(new Map());

  const { data: dynamicText, isInitialLoading: isDynamicTextLoading } = useQuery({
    queryKey: [dynamicTextQuery.key],
    queryFn: dynamicTextQuery.query,
    ...reactQueryConfig,
  });

  const { data: swapCreditModalContent, isInitialLoading: isSwapCreditModalContentLoading } =
    useQuery({
      queryKey: [swapCreditModalContentQuery.key],
      queryFn: swapCreditModalContentQuery.query,
      ...reactQueryConfig,
    });

  const increasePage = () => {
    setSurveyPage(surveyPage + 1);
    return null;
  };

  const upsertMappedAnswers = (newMappedAnswers: Map<number, MappedAnswer>) => {
    setMappedAnswers(newMappedAnswers);
    return null;
  };

  const paginatedSurvey = () => {
    if (survey) return paginate({ survey });
    return new Map();
  };

  if (isCancelFlowSurveyLoading || isDynamicTextLoading || isSwapCreditModalContentLoading)
    return <LoadingSpinner />;

  return (
    <Router>
      <AnalyticsWrapper>
        <Routes>
          <Route
            path={pages.CANCEL_SUBSCRIPTION.path}
            element={
              <PrivateRoute isActive={!!dynamicText?.active}>
                <Layout>
                  <SurveyPageContext.Provider
                    value={{
                      upsertMappedAnswers,
                      mappedAnswers,
                      paginatedSurvey: paginatedSurvey(),
                      surveyPage,
                      increasePage,
                      survey,
                    }}
                  >
                    <DynamicTextContext.Provider
                      value={{ dynamicText, swapCreditModalContent: swapCreditModalContent || [] }}
                    >
                      <KeepSubscriptionsContextProvider>
                        <Container />
                      </KeepSubscriptionsContextProvider>
                    </DynamicTextContext.Provider>
                  </SurveyPageContext.Provider>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={pages.CANCEL_SUBSCRIPTION_FINAL.path}
            element={
              <PrivateRoute isActive={!!dynamicText?.active}>
                <Layout>
                  <DynamicTextContext.Provider
                    value={{ dynamicText, swapCreditModalContent: swapCreditModalContent || [] }}
                  >
                    <CancelSubscriptionFinal
                      subscriptionState={subscriptionState}
                      subscriptionType={subscriptionType}
                    />
                  </DynamicTextContext.Provider>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={pages.SWAP_FOR_CREDIT.path}
            element={
              <PrivateRoute isActive={!!dynamicText?.active}>
                <Layout>
                  <DynamicTextContext.Provider
                    value={{ dynamicText, swapCreditModalContent: swapCreditModalContent || [] }}
                  >
                    <SwapMyBox />
                  </DynamicTextContext.Provider>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
      </AnalyticsWrapper>
    </Router>
  );
};

export default App;
